import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

import { Order } from '../../core/models/order-api.model';
import { I18nService } from '../../core/services/i18n.service';
import { IconComponent } from './icon.component';
import { PriceComponent } from './price.component';
import { OrderStatusComponent } from './order-status.component';
import { TranslatePipe } from '../pipes/translate.pipe';

const ORDER_ICON: Record<string, string> = { delivery: 'solar:scooter-bold', pickup: 'solar:bag-smile-bold', dine_in: 'solar:chair-2-bold' };
const TYPE_LABEL: Record<string, string> = { delivery: 'Delivery', pickup: 'Pickup', dine_in: 'Dine-in' };

/**
 * One order in the order history: the same card on phones (Orders tab) and desktop (Account →
 * Order history). Type icon · "#no · date" · items/type · status badge | total · View.
 * On narrow screens the total + View drop to a footer row under the details.
 */
@Component({
  selector: 'app-order-row',
  standalone: true,
  imports: [IconComponent, PriceComponent, OrderStatusComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    :host { display: block; }
    .or {
      display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; column-gap: 16px;
      border: 1px solid var(--fr-border); border-radius: 18px; background: var(--fr-surface); padding: 20px 22px;
      cursor: pointer; transition: border-color .15s ease, box-shadow .15s ease;
    }
    .or:hover { border-color: var(--fr-border-2); box-shadow: var(--fr-shadow-sm); }
    .ic { width: 46px; height: 46px; border-radius: 13px; background: var(--brand-tint); display: flex; align-items: center; justify-content: center; flex-shrink: 0; align-self: start; }
    .t { font-weight: 750; font-size: 15.5px; color: var(--fr-text); }
    .t .d { font-weight: 600; color: var(--fr-text-2); }
    .s { font-size: 13.5px; color: var(--fr-text-3); margin-top: 3px; line-height: 1.5; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .st { margin-top: 8px; }
    .end { display: flex; align-items: center; gap: 16px; }
    .view {
      height: 40px; padding: 0 16px; border-radius: 11px; border: 1px solid var(--fr-border-2); background: var(--fr-surface); cursor: pointer;
      font-weight: 750; font-size: 13.5px; color: var(--fr-text); font-family: var(--fr-font); display: inline-flex; align-items: center; gap: 6px; white-space: nowrap;
    }
    /* phones: total + View become a footer row under the details */
    @media (max-width: 599px) {
      .or { grid-template-columns: auto minmax(0, 1fr); column-gap: 13px; padding: 15px; border-radius: 16px; }
      .ic { width: 42px; height: 42px; border-radius: 12px; }
      .t { font-size: 14.5px; }
      .s { font-size: 13px; }
      .end { grid-column: 1 / -1; justify-content: space-between; margin-top: 13px; padding-top: 12px; border-top: 1px solid var(--fr-border); }
      .view { height: 38px; }
    }
  `],
  template: `
    <div class="or" (click)="view.emit(order)">
      <span class="ic"><app-icon [name]="icon" [size]="22" color="var(--brand-deep)"></app-icon></span>
      <div style="min-width:0">
        <div class="t fr-num">#{{ no }} · <span class="d">{{ date }}</span></div>
        <div class="s">{{ summary }}</div>
        <app-order-status class="st" [status]="order.status" [deliveryStatus]="order.deliveryStatus"></app-order-status>
      </div>
      <div class="end">
        <app-price [value]="order.bill.total" [size]="16" [weight]="750" [ccy]="order.bill.currencyCode"></app-price>
        <button type="button" class="view" (click)="$event.stopPropagation(); view.emit(order)">{{ 'View' | t }}</button>
      </div>
    </div>`,
})
export class OrderRowComponent {
  @Input({ required: true }) order!: Order;
  @Output() view = new EventEmitter<Order>();

  constructor(private i18n: I18nService) {}

  get icon(): string { return ORDER_ICON[this.order.orderType] || 'solar:bag-check-bold'; }
  get no(): string { return this.order.orderNo != null ? `${this.order.orderNo}` : this.order.reference; }
  get date(): string {
    return this.order.placedAt
      ? new Date(this.order.placedAt).toLocaleDateString(this.i18n.locale, { month: 'short', day: 'numeric', year: 'numeric' })
      : '';
  }
  /** "2× Burger, 1× Fries" when the list carries lines; else the order type. */
  get summary(): string {
    const o = this.order;
    if (o.items?.length) return o.items.map((l) => `${l.quantity}× ${l.name}`).join(', ');
    return this.i18n.t(TYPE_LABEL[o.orderType] || 'Delivery');
  }
}
